import { FC, useState } from 'react';

import { Link, router, usePage } from '@inertiajs/react';
import { Contact, Package, Truck } from 'lucide-react';
import { Clock } from 'lucide-react';
import { User } from 'lucide-react';

import { BuyerOrderEditDialog } from './BuyerOrderEditDialog';
import { ConfirmCancelModal } from './ConfirmCancelModal';
import { ConfirmCancelModalState } from './ConfirmCancelModal/ConfirmCancelModal.types';
import { OrderItemsList } from './OrderItemsList';
import { OrderReviewDialog } from './OrderReviewDialog';
import { OrderReviewSummary } from './OrderReviewSummary';
import { OrderRevisionPanel } from './OrderRevisionPanel';
import { formatOrderAmount } from './orderRevisionUtils';
import { getStatusNoticeTone } from './orderStatusNoticeTone';
import { Pagination } from './Pagination';
import { PurchasesDeliveryDetails } from './PurchasesDeliveryDetails';
import { PurchasesStatusPanel } from './PurchasesStatusPanel';
import { Badge } from '../../../components/ui/Badge';
import { Button } from '../../../components/ui/Button';
import { Card, CardContent } from '../../../components/ui/Card';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '../../../components/ui/Tooltip';
import { Routes, profileOrderActionRoute } from '../../../models/routes';
import { OrdersService } from '../../../services/OrdersService';
import { OrderCancellationReason, OfferProperty, Order } from '../../../types';
import { formatLocalDateTime } from '../../../utils';

type PageProps = {
    orders: Order[] | null;
    offerProperties: OfferProperty[];
    cancellationReasons: OrderCancellationReason[];
    nextPage: number | null;
};

const shouldShowSellerTelegram = (order: Order) =>
    Boolean(
        order.seller?.telegram_username &&
            order.order_items?.some((item) => item.status === 'approved'),
    );

const Purchases: FC = () => {
    const { orders, offerProperties, cancellationReasons, nextPage } =
        usePage<PageProps>().props;
    const ordersList = orders || [];

    const [cancelModalState, setCancelModalState] =
        useState<ConfirmCancelModalState>({
            open: false,
            orderId: null,
            action: 'cancel',
        });
    const [reviewOrder, setReviewOrder] = useState<Order | null>(null);
    const [editingOrder, setEditingOrder] = useState<Order | null>(null);

    const handleConfirmReceived = (orderId: number) => {
        router.post(profileOrderActionRoute(orderId, 'confirm_received'));
    };

    return (
        <TooltipProvider>
            {cancelModalState.open && (
                <ConfirmCancelModal
                    state={cancelModalState}
                    setState={setCancelModalState}
                    reasons={cancellationReasons}
                />
            )}
            <OrderReviewDialog
                order={reviewOrder}
                open={Boolean(reviewOrder)}
                onOpenChange={(open) => {
                    if (!open) {
                        setReviewOrder(null);
                    }
                }}
            />
            <BuyerOrderEditDialog
                order={editingOrder}
                open={Boolean(editingOrder)}
                onOpenChange={(open) => {
                    if (!open) setEditingOrder(null);
                }}
            />
            <div className="space-y-6 py-6">
                <div className="flex flex-col gap-0">
                    <h1 className="text-2xl font-bold sm:text-3xl">Покупки</h1>
                </div>

                {(!orders || orders.length === 0) && (
                    <div className="mt-10 rounded-2xl border bg-muted/10 py-12 text-center">
                        <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-muted">
                            <Package className="h-6 w-6 text-muted-foreground" />
                        </div>
                        <h3 className="mb-1 text-lg font-medium">
                            Покупки не знайдено
                        </h3>
                        <p className="mb-4 text-sm text-muted-foreground">
                            У вас ще немає покупок на сайті.
                        </p>
                        <Button asChild className="mt-4">
                            <Link href={Routes.Home}>
                                <Package className="mr-2 h-4 w-4" />
                                До покупок
                            </Link>
                        </Button>
                    </div>
                )}

                {ordersList.length > 0 && (
                    <div className="mb-6 space-y-4">
                        {ordersList.map((order) => {
                            const statusNoticeTone = getStatusNoticeTone(order);

                            return (
                                <Card
                                    key={order.id}
                                    className="overflow-hidden transition-shadow hover:shadow-md"
                                >
                                    <CardContent className="p-0">
                                        <div className="flex items-start gap-4 p-4">
                                            <div className="mt-1 hidden flex-shrink-0 rounded-full bg-primary/10 p-2 md:block">
                                                <Package className="h-4 w-4 text-primary" />
                                            </div>

                                            <div className="min-w-0 flex-1">
                                                <div className="mb-2 flex items-center justify-between">
                                                    <div className="flex items-center gap-3">
                                                        <h3 className="text-sm font-semibold">
                                                            {order.number}
                                                        </h3>
                                                        {order.status ===
                                                        'awaiting_shipment' ? (
                                                            <Tooltip
                                                                delayDuration={
                                                                    0
                                                                }
                                                            >
                                                                <TooltipTrigger
                                                                    asChild
                                                                >
                                                                    <Badge
                                                                        variant={OrdersService.getStatusBadgeVariant(
                                                                            order.status,
                                                                            order.delivery,
                                                                        )}
                                                                        className="cursor-help text-xs"
                                                                    >
                                                                        {OrdersService.getStatusLabel(
                                                                            order.status,
                                                                            order.delivery,
                                                                        )}
                                                                    </Badge>
                                                                </TooltipTrigger>
                                                                <TooltipContent className="border-orange-200 bg-orange-50 text-orange-600">
                                                                    <p>
                                                                        {order
                                                                            .delivery
                                                                            ?.method ===
                                                                        'pickup'
                                                                            ? 'Продавець готує замовлення до особистої передачі.'
                                                                            : 'Продавець готує замовлення до відправки.'}
                                                                    </p>
                                                                </TooltipContent>
                                                            </Tooltip>
                                                        ) : (
                                                            <Badge
                                                                variant={OrdersService.getStatusBadgeVariant(
                                                                    order.status,
                                                                    order.delivery,
                                                                )}
                                                                className="text-xs"
                                                            >
                                                                {OrdersService.getStatusLabel(
                                                                    order.status,
                                                                    order.delivery,
                                                                )}
                                                            </Badge>
                                                        )}
                                                    </div>
                                                    <div className="text-right">
                                                        <div className="font-semibold">
                                                            {formatOrderAmount(
                                                                order.total_price ||
                                                                    0,
                                                            )}
                                                        </div>
                                                        <div className="text-xs text-muted-foreground">
                                                            {
                                                                order
                                                                    .order_items
                                                                    ?.length
                                                            }{' '}
                                                            {order.order_items
                                                                ?.length === 1
                                                                ? 'товар'
                                                                : 'товари'}
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="mb-3 flex flex-wrap gap-x-4 gap-y-1 text-xs text-muted-foreground">
                                                    <div className="flex items-center gap-1">
                                                        <Clock className="h-3 w-3" />
                                                        {formatLocalDateTime(
                                                            order.created_at,
                                                        )}
                                                    </div>
                                                    {order.seller && (
                                                        <div className="flex items-center gap-1">
                                                            <User className="h-3 w-3" />
                                                            Продавець:{' '}
                                                            <a
                                                                href={`/user/${order.seller.nickname}`}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="font-semibold hover:underline"
                                                            >
                                                                {
                                                                    order.seller
                                                                        .nickname
                                                                }
                                                            </a>
                                                        </div>
                                                    )}
                                                    {shouldShowSellerTelegram(
                                                        order,
                                                    ) && (
                                                        <div className="flex items-center gap-1">
                                                            <Contact className="h-3 w-3" />
                                                            Telegram:{' '}
                                                            <a
                                                                href={`https://t.me/${order.seller.telegram_username.replace('@', '')}`}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                className="font-semibold hover:underline"
                                                            >
                                                                {
                                                                    order.seller
                                                                        .telegram_username
                                                                }
                                                            </a>
                                                        </div>
                                                    )}
                                                    {order.tracking_number && (
                                                        <div className="flex items-center gap-1">
                                                            <Truck className="h-3 w-3" />
                                                            Трек-номер:{' '}
                                                            {
                                                                order.tracking_number
                                                            }
                                                        </div>
                                                    )}
                                                </div>

                                                <PurchasesDeliveryDetails
                                                    order={order}
                                                />

                                                <OrderItemsList
                                                    items={order.order_items}
                                                    label="Куплені товари:"
                                                    offerProperties={
                                                        offerProperties
                                                    }
                                                />

                                                <OrderRevisionPanel
                                                    order={order}
                                                    role="buyer"
                                                />

                                                <PurchasesStatusPanel
                                                    order={order}
                                                    tone={statusNoticeTone}
                                                    onConfirmReceived={
                                                        handleConfirmReceived
                                                    }
                                                    onReviewOrder={
                                                        setReviewOrder
                                                    }
                                                    onCancelOrder={(orderId) =>
                                                        setCancelModalState({
                                                            open: true,
                                                            orderId,
                                                            action: 'cancel',
                                                        })
                                                    }
                                                    onEditOrder={
                                                        setEditingOrder
                                                    }
                                                />

                                                {order.review && (
                                                    <OrderReviewSummary
                                                        review={order.review}
                                                        title="Ваш відгук до цього замовлення"
                                                    />
                                                )}
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>
                            );
                        })}
                    </div>
                )}

                {ordersList.length > 0 && (
                    <Pagination url={Routes.Purchases} nextPage={nextPage} />
                )}
            </div>
        </TooltipProvider>
    );
};

export default Purchases;
